/**
 * URL-querystring-driven filter state, shared by the Board and List views.
 *
 * The querystring *is* the state, so any view is shareable by copying its URL.
 * Axis names match the API query params in `todo_api/routers/tickets.py`
 * exactly, which is what lets `filtersToParams` hand them straight to fetch.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/** Repeatable filter axes, in display order. */
export const AXES = ['city', 'component', 'assignee', 'kind', 'priority', 'status']

export const STATUSES = [
  'backlog',
  'todo',
  'in_progress',
  'blocked',
  'review',
  'done',
  'wont_do',
]

/** Columns rendered on the board, left to right. */
export const BOARD_COLUMNS = STATUSES

export const KINDS = ['task', 'bug', 'scope-decision', 'go-live-gate']
export const PRIORITIES = ['p0', 'p1', 'p2', 'p3']

export const STATUS_LABELS = {
  backlog: 'Backlog',
  todo: 'Todo',
  in_progress: 'In progress',
  blocked: 'Blocked',
  review: 'Review',
  done: 'Done',
  wont_do: "Won't do",
}

/**
 * The status-transition table, owned entirely by the server.
 *
 * There is deliberately no client-side copy: a hand-mirrored table is how the
 * two drift. `App` fetches `GET /api/v1/meta` on mount and calls
 * `setTransitions()` with the server's table; until that answers this is
 * `null` and `canTransition` reports every move as unavailable, so the UI can
 * never offer a move the server would reject. The window is one request long.
 *
 * @type {Record<string, string[]> | null}
 */
let liveTransitions = null

/**
 * Install the server's transition table as the one every view checks against.
 *
 * @param {Record<string, string[]>} transitions From `GET /api/v1/meta`.
 */
export function setTransitions(transitions) {
  if (transitions && Object.keys(transitions).length) liveTransitions = transitions
}

/**
 * The transition table currently in force, or `{}` before `/meta` has answered.
 *
 * @returns {Record<string, string[]>}
 */
export function getTransitions() {
  return liveTransitions || {}
}

/**
 * Whether `to` is a legal destination status for a ticket currently in `from`.
 *
 * @param {string} from Current status.
 * @param {string} to Candidate status.
 * @returns {boolean} `false` for every move while the server's table is still
 *   in flight — the UI dims rather than guesses.
 */
export function canTransition(from, to) {
  if (from === to) return true
  if (!liveTransitions) return false
  return (liveTransitions[from] || []).includes(to)
}

/**
 * Read the filter state out of a `URLSearchParams`.
 *
 * @param {URLSearchParams} search
 * @returns {{city: string[], component: string[], assignee: string[], kind: string[], priority: string[], status: string[], q: string, sort: string}}
 */
export function readFilters(search) {
  const filters = { q: search.get('q') || '', sort: search.get('sort') || '-updated_at' }
  for (const axis of AXES) filters[axis] = search.getAll(axis)
  return filters
}

/** Convert filter state into the query params the tickets endpoint expects. */
export function filtersToParams(filters, extra = {}) {
  const params = { limit: 500, sort: filters.sort || '-updated_at', ...extra }
  for (const axis of AXES) if (filters[axis]?.length) params[axis] = filters[axis]
  if (filters.q) params.q = filters.q
  return params
}

/** Number of active axis selections plus the text query. */
export function activeFilterCount(filters) {
  return AXES.reduce((n, axis) => n + (filters[axis]?.length || 0), 0) + (filters.q ? 1 : 0)
}

/**
 * React hook giving a view its filter state and mutators, backed by the URL.
 *
 * @returns {{filters: object, search: URLSearchParams, toggle: Function, setAxis: Function, setValue: Function, clear: Function}}
 */
export function useFilters() {
  const [search, setSearch] = useSearchParams()
  const filters = useMemo(() => readFilters(search), [search])

  const setAxis = useCallback(
    (axis, values) => {
      const next = new URLSearchParams(search)
      next.delete(axis)
      for (const v of values) next.append(axis, v)
      setSearch(next, { replace: true })
    },
    [search, setSearch],
  )

  const toggle = useCallback(
    (axis, value) => {
      const current = search.getAll(axis)
      setAxis(axis, current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
    },
    [search, setAxis],
  )

  const setValue = useCallback(
    (key, value) => {
      const next = new URLSearchParams(search)
      if (value) next.set(key, value)
      else next.delete(key)
      setSearch(next, { replace: true })
    },
    [search, setSearch],
  )

  const clear = useCallback(() => setSearch(new URLSearchParams(), { replace: true }), [setSearch])

  return { filters, search, toggle, setAxis, setValue, clear }
}
